import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { Badge } from "@/uiCore";
import { Stack } from "@altimateai/lego";
import { GetSnowflakeJobRunItem } from "@/pages/query/types";
import { FC, useMemo, useState } from "react";
import { useAppState } from "@/modules/app/useAppContext";
import { formatCost, formatMilliseconds } from "@/helpers/utils";
import styles from "@/pages/query/styles.module.scss";
import dayjs from "dayjs";
import classNames from "classnames";
import { Code4Icon, InfoIcon, StatusIcon, Warehouse3Icon } from "@assets/icons";
import { getPercentKey, isPercentKey } from "@/pages/query/common";
import { Spinner } from "@altimateai/lego";

interface GraphData {
  run_id?: string;
  timestamp: string;
  start_time?: string;
  query_cost: number;
  execution_time: number;
  annotation?: GetSnowflakeJobRunItem["annotation"];
}

export const PercentChange = ({ percentChange }: { percentChange: number | undefined }) => {
  if (!percentChange) return null;
  const isPositive = percentChange >= 0;
  return (
    <span className={isPositive ? "negative" : "positive"}>
      {isPositive ? "+" : ""}
      {(percentChange * 100).toFixed(2)}%
    </span>
  );
};

export const CostChartTooltip: FC<TooltipProps<number, string> & { aggregateBy?: string }> = ({
  payload,
  aggregateBy,
}) => {
  const { currency } = useAppState();
  const data = payload && payload[0] && payload[0].payload;

  if (!data) {
    return null;
  }

  return (
    <div className={styles.costChartTooltip}>
      <h6>
        {dayjs(data.date).format("DD-MMM-YY")} {aggregateBy ? `(Aggregation: ${aggregateBy})` : ""}
      </h6>
      <Badge tooltip="">
        Total Cost:{" "}
        {formatCost(
          payload
            .filter(item => !isPercentKey(item.dataKey))
            .reduce((acc, item) => acc + (item.value ?? 0), 0),
          currency
        )}
      </Badge>
      <ul>
        {payload
          .filter(item => !isPercentKey(item.dataKey))
          .map(item => {
            const percentChange = item.payload[getPercentKey(item.dataKey)];
            return (
              <li key={item.dataKey}>
                <Stack align="center">
                  <div className={styles.sticker} style={{ background: item.color }} />
                  <div>{item?.name || item.dataKey}:</div>
                  <div>{formatCost(item.value ?? 0, currency)}</div>
                  <PercentChange percentChange={percentChange} />
                </Stack>
              </li>
            );
          })}
      </ul>
    </div>
  );
};

export const CustomTooltip: FC<TooltipProps<number, string>> = ({ active, payload }) => {
  const { currency } = useAppState();
  if (!(active && payload && payload.length)) {
    return null;
  }
  const queryCostItem = payload.find(i => i.dataKey === "query_cost");
  const executionTimeItem = payload.find(i => i.dataKey === "execution_time");
  const items = [];
  if (queryCostItem) {
    items.push({
      value: formatCost(
        // @ts-ignore
        queryCostItem.value,
        currency
      ),
      key: "Cost",
      color: queryCostItem.color,
    });
  }
  if (executionTimeItem) {
    items.push({
      value: formatMilliseconds(
        // @ts-ignore
        executionTimeItem.value
      ),
      key: "Runtime",
      color: executionTimeItem.color,
    });
  }
  if (payload[0].payload.task_id) {
    items.push({
      value: payload[0].payload.task_id,
      key: "Task Id",
      color: payload[0].color,
    });
  }
  if (payload[0].payload.run_id) {
    items.push({
      value: payload[0].payload.run_id,
      key: "Run Id",
      color: payload[0].color,
    });
  }
  const annotation = payload[0].payload.annotation;
  return (
    <div className={styles.chart_tooltip}>
      <h6>
        {dayjs(
          payload[0].payload.start_time || payload[0].payload.timestamp?.split("_")[0]
        ).format("DD-MMM-YY")}
      </h6>
      {items.map(item => (
        <Stack key={item.key} align="center" gap="sm">
          <div className={styles.sticker} style={{ background: item.color }} />
          <div className={styles.tooltip_header}>{item.key}:</div>
          <div className={styles.tooltip_execs}>{item.value}</div>
        </Stack>
      ))}
      <div className="mt-2" />
      {annotation?.warehouse_change && (
        <div className={classNames(styles.annotation, styles.warehouse_change)}>
          <InfoIcon />
          {annotation?.warehouse_change}
        </div>
      )}
      {annotation?.code_change && (
        <div className={classNames(styles.annotation, styles.code_change)}>
          <InfoIcon />
          {annotation?.code_change}
        </div>
      )}
      {annotation?.execution_status && (
        <div className={classNames(styles.annotation, styles.execution_status)}>
          <InfoIcon />
          {annotation?.execution_status}
        </div>
      )}
    </div>
  );
};

const CustomXAxisTick = ({
  x = 0,
  y = 0,
  payload = { value: "" },
  annotationEnabled = false,
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
  annotationEnabled?: boolean;
}) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={annotationEnabled ? 15 : 10} transform="rotate(20)" fill="#66768D">
        {dayjs(payload.value.split("_")[0]).format("MMM DD")}
      </text>
    </g>
  );
};

const CustomYAxisTick = ({
  x = 0,
  y = 0,
  payload = { value: "" },
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
}) => (
  <g transform={`translate(${x},${y})`}>
    <text textAnchor="end" dx={24} dy={5} fill="#66768D">
      {(Number(payload.value) / (1000 * 60)).toFixed(0)}
    </text>
  </g>
);

const CustomDot = (props: {
  cx: number;
  cy: number;
  annotation: GetSnowflakeJobRunItem["annotation"];
}) => {
  const items = [];
  const getX = () => props.cx - 10;
  const getY = () => props.cy - 10 - items.length * 6;
  if (props.annotation?.warehouse_change) {
    items.push(<Warehouse3Icon x={getX()} y={getY()} width={20} height={20} />);
  }

  if (props.annotation?.execution_status) {
    items.push(<StatusIcon x={getX()} y={getY()} width={20} height={20} />);
  }

  if (props.annotation?.code_change) {
    items.push(<Code4Icon x={getX()} y={getY()} width={20} height={20} />);
  }

  return items.reverse();
};

export const Graph: React.FC<{
  data: GraphData[];
  setSelectedRun?: (k: string) => void;
  annotationEnabled?: boolean;
  isLoading?: boolean;
}> = ({ data, setSelectedRun, annotationEnabled = false, isLoading = false }) => {
  const [focusedLine, setFocusedLine] = useState<string | null>(null);

  const handleLegendClick = (payload: { dataKey?: unknown }) => {
    const key = payload.dataKey;
    if (typeof key === "string") {
      setFocusedLine(prev => (prev === key ? null : key));
    }
  };

  const legendFormatter = (value: string, entry: { dataKey?: unknown }) => {
    const key = entry.dataKey;
    if (focusedLine && typeof key === "string" && focusedLine !== key) {
      return <span style={{ color: "#ccc" }}>{value}</span>;
    }
    return value;
  };

  const graphData = useMemo(() => {
    return data.map(d => {
      return {
        ...d,
        timestamp: d.timestamp
          ? `${d.timestamp}_${crypto.randomUUID()}`
          : `${d.start_time}_${crypto.randomUUID()}`,
      };
    });
  }, [data]);

  const xAxisInterval = useMemo(
    () => Math.max(0, Math.floor(graphData.length / 15)),
    [graphData.length]
  );

  return (
    <>
      {!isLoading && graphData.length > 0 ? (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={graphData}
            margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
            onClick={e => setSelectedRun?.(e.activePayload?.[0].payload.run_id)}
          >
            <CartesianGrid strokeDasharray="3" />
            <XAxis
              interval={xAxisInterval}
              dataKey="timestamp"
              tick={<CustomXAxisTick annotationEnabled={annotationEnabled} />}
            />
            <YAxis
              yAxisId="left"
              label={{ value: "Cost ($)", angle: -90, position: "insideLeft" }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              label={{
                value: "Run Time (mins)",
                angle: -90,
                position: "insideRight",
              }}
              tick={<CustomYAxisTick />}
            />
            {/* @ts-ignore */}
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              wrapperStyle={{ bottom: annotationEnabled ? 4 : 0 }}
              onClick={handleLegendClick}
              formatter={legendFormatter}
            />
            <Line
              hide={focusedLine !== null && focusedLine !== "query_cost"}
              yAxisId="left"
              type="monotone"
              dataKey="query_cost"
              stroke="#247EFE"
              strokeWidth={4}
              dot={{ r: 0 }}
              activeDot={{ r: 8 }}
              name="Cost"
            />
            <Line
              hide={focusedLine !== null && focusedLine !== "execution_time"}
              yAxisId="right"
              type="monotone"
              dataKey="execution_time"
              stroke="#9382D9"
              strokeWidth={4}
              dot={{ r: 0 }}
              activeDot={{ r: 8 }}
              name="Run Time"
            />
            {/* Annotations (Reference dots for events) */}
            {annotationEnabled &&
              graphData
                .filter(point => Boolean(point.annotation))
                .map(point => (
                  <ReferenceDot
                    key={point.run_id + "-" + crypto.randomUUID()}
                    x={point.timestamp}
                    y={0}
                    yAxisId="left"
                    r={5}
                    shape={(dotProps: { cx: number; cy: number }) => (
                      <CustomDot {...dotProps} annotation={point.annotation ?? null} />
                    )}
                  />
                ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <Stack
          align={"center"}
          justify={"center"}
          className={"al-w-full al-border al-rounded-md al-min-h-32"}
        >
          {isLoading ? (
            <Stack>
              <Spinner size={"md"} />
              Loading graph...
            </Stack>
          ) : (
            <div className={"al-text-light-gray al-text-center al-p-4"}>No data is present</div>
          )}
        </Stack>
      )}
    </>
  );
};
